import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LandPlotsCoordinatesBlock } from "./master-plan-modification";
import Polygon from "@arcgis/core/geometry/Polygon";
import { convertPolygonToString } from "../../utils/master-plan-helper";
import { planarArea } from "@arcgis/core/geometry/geometryEngine";
import { editFeaturesOnMap } from "../actions/master-plan-modification";
import MapView from "@arcgis/core/views/MapView";
import Graphic from "@arcgis/core/Graphic";
import {
  BufferInfrastructureResults,
  GeometryObject,
  LogisticsResults,
  MainFormData,
  PhaseTerritory,
  StudyCreationOptionsState,
} from "../../interfaces/master-create-study";
import { InfrastructureTemplates } from "../../components/features/master-create-study/main-form-create-study/conts";

export interface CreatedObjectInfrastructure {
  distance: number | null;
  direction: string | null;
}

export type InfrastructureTypeKey = keyof typeof InfrastructureTemplates;

export type CreatedObjectInfrastructureResults = Partial<
  Record<InfrastructureTypeKey, CreatedObjectInfrastructure>
>;

const createdObject = [
  {
    objectId: Date.now(),
    newGeometry: new Polygon({
      rings: [],
      spatialReference: { wkid: 32640 },
    }),
    stringGeometry: {
      rings: [],
      wkid: 32640,
    },
    wkid: 32640,
  },
];

const SpatialReferenceMap = new Map<number, string>([
  [32639, "WGS 84 / UTM zone 39N"],
  [32640, "WGS 84 / UTM zone 40N"],
]);

const statuses = [
  "Draft",
  "Under Review",
  "Rejected",
  "Approved",
  "Rejected by MOHUP",
  "Approved by MOHUP",
];

const statusOptions = new Map<string, string>(
  statuses.map((status) => [status, status]),
);

export interface StudyCreationState {
  mainForm: MainFormData;
  options: StudyCreationOptionsState;
  createdObject: GeometryObject[];
  coordinatesData: LandPlotsCoordinatesBlock | null;
  spatialReferenceOptions: Map<number, string>;
  viewer: MapView | null;
  selectedFeature: Graphic | null;
  phaseTerritory: PhaseTerritory;
  bufferInfrastructureResults: BufferInfrastructureResults;
  externalServicesAvailability: {
    opis: boolean;
    ncsi: boolean;
  };
  logisticsResults: LogisticsResults;
  createdObjectInfrastructureResults: CreatedObjectInfrastructureResults;
}

const initialState: StudyCreationState = {
  mainForm: {
    drafter: "",
    status: "Draft",
    name: "",
    governorate: "",
    buffer: "",
  },
  options: {
    statusOptions: statusOptions,
    governoratesOptions: new Map<string, string>([
      ["Ad Dakhiliyah", "Ad Dakhiliyah"],
      ["Ad Dhahirah", "Ad Dhahirah"],
      ["Al Batinah North", "Al Batinah North"],
      ["Al Batinah South", "Al Batinah South"],
      ["Al Buraimi", "Al Buraimi"],
      ["Al Wusta", "Al Wusta"],
      ["Ash Sharqiyah North", "Ash Sharqiyah North"],
      ["Ash Sharqiyah South", "Ash Sharqiyah South"],
      ["Dhofar", "Dhofar"],
      ["Muscat", "Muscat"],
      ["Musandam", "Musandam"],
    ]),
  },
  createdObject: createdObject,
  coordinatesData: null,
  spatialReferenceOptions: SpatialReferenceMap,
  viewer: null,
  selectedFeature: null,
  phaseTerritory: {
    graphic: null,
    visible: true,
    area: 0,
  },
  bufferInfrastructureResults: {
    flatTopography: {
      label: "a. Flat topography",
      available: false,
      distance: 0,
    },
    wadi: { label: "b. Wadi", available: false, distance: 0 },
    powerSupply: { label: "c. Power supply", available: false, distance: 0 },
    water: { label: "d. Water", available: false, distance: 0 },
    telecom: { label: "e. Telecom", available: false, distance: 0 },
    drainage: { label: "f. Drainage", available: false, distance: 0 },
    gas: { label: "g. Gas", available: false, distance: 0 },
  },
  externalServicesAvailability: {
    opis: false,
    ncsi: false,
  },
  logisticsResults: {
    airportDistance: null,
    FirstPortName: null,
    FirstPortDist: null,
    SecondPortName: null,
    SecondPortDist: null,
  },
  createdObjectInfrastructureResults: {},
};

export const studyCreationSlice = createSlice({
  name: "studyCreation",
  initialState,
  reducers: {
    updateMainForm(state, action: PayloadAction<Partial<MainFormData>>) {
      state.mainForm = { ...state.mainForm, ...action.payload };
    },
    setGeometries: (
      state,
      action: PayloadAction<{
        newGeometry: Map<number, Polygon>;
        stringGeometry?: Map<number, { rings: string[][][]; wkid: number }>;
      }>,
    ) => {
      state.createdObject = state.createdObject.map((obj) => {
        {
          const geometry = action.payload.newGeometry.has(obj.objectId)
            ? action.payload.newGeometry.get(obj.objectId)!
            : null;
          const stringGeometry =
            action.payload.stringGeometry?.get(obj.objectId) ??
            (geometry ? convertPolygonToString(geometry) : undefined);

          const newWkid =
            stringGeometry?.wkid ?? geometry?.spatialReference.wkid ?? obj.wkid;

          return {
            ...obj,
            newGeometry: geometry,
            stringGeometry,
            area: geometry
              ? planarArea(geometry, "square-meters").toFixed(2)
              : "0",
            wkid: newWkid,
          };
        }
      });
    },
    setCoordinatesData: (
      state,
      action: PayloadAction<LandPlotsCoordinatesBlock | null>,
    ) => {
      state.coordinatesData = action.payload;
    },
    resetCreatedObject: (state) => {
      state.createdObject = [...initialState.createdObject];
      state.coordinatesData = initialState.coordinatesData;
    },
    setViewer: (state, action: PayloadAction<MapView>) => {
      state.viewer = action.payload as any;
    },
    setSelectedFeature: (state, action: PayloadAction<Graphic | null>) => {
      state.selectedFeature = action.payload;
    },
    setPhaseTerritoryGraphic: (
      state,
      action: PayloadAction<Graphic | null>,
    ) => {
      state.phaseTerritory.graphic = action.payload;

      if (
        action.payload?.geometry &&
        action.payload.geometry.type === "polygon"
      ) {
        state.phaseTerritory.area = planarArea(
          action.payload.geometry as Polygon,
          "square-meters",
        );
      } else {
        state.phaseTerritory.area = 0;
      }
    },
    setPhaseTerritoryVisible: (state, action: PayloadAction<boolean>) => {
      state.phaseTerritory.visible = action.payload;
    },
    setBufferInfrastructureResults(
      state,
      action: PayloadAction<BufferInfrastructureResults>,
    ) {
      const updatedResults = action.payload;
      (
        Object.keys(updatedResults) as Array<keyof BufferInfrastructureResults>
      ).forEach((serviceKey) => {
        const newInfo = updatedResults[serviceKey];
        const existingInfo = state.bufferInfrastructureResults[serviceKey];
        existingInfo.available = newInfo.available;
        existingInfo.distance = newInfo.distance;
      });
    },
    setExternalServicesAvailability: (
      state,
      action: PayloadAction<{ opis: boolean; ncsi: boolean }>,
    ) => {
      state.externalServicesAvailability = action.payload;
    },
    setLogisticsResults: (state, action: PayloadAction<LogisticsResults>) => {
      state.logisticsResults = action.payload;
    },
    setCreatedObjectInfrastructureResults: (
      state,
      action: PayloadAction<CreatedObjectInfrastructureResults>,
    ) => {
      state.createdObjectInfrastructureResults = action.payload;
    },
    resetAllAnalysisResults(state) {
      state.bufferInfrastructureResults =
        initialState.bufferInfrastructureResults;
      state.externalServicesAvailability =
        initialState.externalServicesAvailability;
      state.logisticsResults = initialState.logisticsResults;
      state.createdObjectInfrastructureResults =
        initialState.createdObjectInfrastructureResults;
    },
    resetStudyCreationState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(editFeaturesOnMap, (state, action) => {
      state.createdObject = state.createdObject.map((plot) => {
        {
          const geometry = action.payload.has(plot.objectId)
            ? action.payload.get(plot.objectId)!
            : null;
          return {
            ...plot,
            newGeometry: geometry,
            stringGeometry: geometry
              ? convertPolygonToString(geometry)
              : undefined,
            area: geometry
              ? planarArea(geometry, "square-meters").toFixed(2)
              : "0",
          };
        }
      });
    });
  },
});

export const {
  updateMainForm,
  setGeometries,
  setCoordinatesData,
  resetCreatedObject,
  setViewer,
  resetStudyCreationState,
  setSelectedFeature,
  setPhaseTerritoryGraphic,
  setPhaseTerritoryVisible,
  setBufferInfrastructureResults,
  resetAllAnalysisResults,
  setExternalServicesAvailability,
  setLogisticsResults,
  setCreatedObjectInfrastructureResults,
} = studyCreationSlice.actions;

export default studyCreationSlice.reducer;
